import { Calendar, Lightbulb } from "lucide-react";
import { useEffect, useState } from "react";
import Joyride, { CallBackProps, STATUS, Step } from "react-joyride";
import { useNavigate } from "react-router-dom";
import {
	AlertDialog,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	Button,
} from "@/components/primitives";
import { useAuth } from "@/contexts/AuthContext";

interface OnboardingTutorialProps {
	hasReflections: boolean;
	hasCheckIns: boolean;
}

const TUTORIAL_STORAGE_KEY = "onboarding_tutorial_dismissed";
const TUTORIAL_SESSION_KEY = "onboarding_tutorial_shown_this_session";

export function OnboardingTutorial({
	hasReflections,
	hasCheckIns,
}: OnboardingTutorialProps) {
	const navigate = useNavigate();
	const { profile, updateProfile } = useAuth();
	const [showIntroDialog, setShowIntroDialog] = useState(false);
	const [showCompletionDialog, setShowCompletionDialog] = useState(false);
	const [runTutorial, setRunTutorial] = useState(false);

	useEffect(() => {
		// Wait for profile to load so we can respect DB flags
		if (profile === null) return;

		// Only show tutorial if user has accepted terms and conditions first
		if (!profile?.tnc_accepted) return;

		// Only show if user has no reflections or check-ins AND hasn't completed/dismissed
		const shouldShowContentGate = !hasReflections && !hasCheckIns;
		const profileBlocked = !!(
			profile?.tutorial_intro_completed || profile?.tutorial_intro_dismissed
		);
		const isDismissedForever =
			localStorage.getItem(TUTORIAL_STORAGE_KEY) === "true";
		const shownThisSession =
			sessionStorage.getItem(TUTORIAL_SESSION_KEY) === "true";

		if (
			shouldShowContentGate &&
			!profileBlocked &&
			!isDismissedForever &&
			!shownThisSession
		) {
			const timer = setTimeout(() => {
				setShowIntroDialog(true);
				sessionStorage.setItem(TUTORIAL_SESSION_KEY, "true");
			}, 1000);
			return () => clearTimeout(timer);
		}
	}, [hasReflections, hasCheckIns, profile]);

	const handleDismissForever = async () => {
		localStorage.setItem(TUTORIAL_STORAGE_KEY, "true");
		setShowIntroDialog(false);
		try {
			await updateProfile({
				tutorial_intro_dismissed: true,
				updated_at: new Date().toISOString(),
			});
		} catch {}
	};

	const handleSkipThisTime = () => {
		setShowIntroDialog(false);
	};

	const handleStartTutorial = () => {
		setShowIntroDialog(false);
		setRunTutorial(true);
	};

	const handleJoyrideCallback = async (data: CallBackProps) => {
		const { status } = data;
		const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED];

		if (finishedStatuses.includes(status)) {
			setRunTutorial(false);
			if (status === STATUS.FINISHED) {
				setShowCompletionDialog(true);
				// Persist completion to DB and locally (fallback)
				try {
					await updateProfile({
						tutorial_intro_completed: true,
						tutorial_intro_completed_at: new Date().toISOString(),
						updated_at: new Date().toISOString(),
					});
				} catch {}
				try {
					localStorage.setItem(TUTORIAL_STORAGE_KEY, "true");
				} catch {}
			}
		}
	};

	const handleCreateReflection = () => {
		setShowCompletionDialog(false);
		navigate("/reflection/new");
	};

	const handleCreateCheckIn = () => {
		setShowCompletionDialog(false);
		navigate("/daily-checkin");
	};

	const handleCompletionLater = () => {
		setShowCompletionDialog(false);
	};

	const steps: Step[] = [
		{
			target: "body",
			content: (
				<div className="space-y-3">
					<h3 className="text-lg font-semibold">
						Welcome to Your Reflection Journal!
					</h3>
					<p>
						Let's take a quick tour to help you get started with tracking your
						personal growth.
					</p>
				</div>
			),
			placement: "center",
			disableBeacon: true,
		},
		{
			target: '[data-tutorial="create-reflection"]',
			content: (
				<div className="space-y-2">
					<div className="flex items-center gap-2 mb-2">
						<Lightbulb className="h-5 w-5 text-primary" />
						<h3 className="text-base font-semibold">Create a Reflection</h3>
					</div>
					<p className="text-sm">
						Reflections help you analyze important experiences, challenges, or
						successes. They guide you through understanding what happened, why
						it happened, and what you can learn from it.
					</p>
					<p className="text-sm text-muted-foreground mt-2">
						Perfect for deeper thinking after a significant event or when you
						want to document lessons learned.
					</p>
				</div>
			),
			placement: "bottom",
			disableBeacon: true,
		},
		{
			target: '[data-tutorial="create-checkin"]',
			content: (
				<div className="space-y-2">
					<div className="flex items-center gap-2 mb-2">
						<Calendar className="h-5 w-5 text-primary" />
						<h3 className="text-base font-semibold">Add a Check-in</h3>
					</div>
					<p className="text-sm">
						Daily check-ins help you track your mood, energy levels, and daily
						progress. They take just a minute and give you valuable insights
						into your patterns over time.
					</p>
					<p className="text-sm text-muted-foreground mt-2">
						Use check-ins to maintain daily awareness and spot trends in your
						well-being.
					</p>
				</div>
			),
			placement: "bottom",
			disableBeacon: true,
		},
		{
			target: "body",
			content: (
				<div className="space-y-3">
					<h3 className="text-lg font-semibold">You're All Set!</h3>
					<p>
						Start with a daily check-in to track your mood, or create a
						reflection to analyze a recent experience.
					</p>
					<p className="text-sm text-muted-foreground">
						You can always access these features from the buttons at the top of
						your dashboard.
					</p>
				</div>
			),
			placement: "center",
			disableBeacon: true,
		},
	];

	return (
		<>
			{/* Intro Dialog */}
			<AlertDialog open={showIntroDialog} onOpenChange={setShowIntroDialog}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Welcome! Want a Quick Tour?</AlertDialogTitle>
						<AlertDialogDescription>
							We've created a short tutorial to help you get started with your
							Reflection Journal. It will show you how to create reflections and
							daily check-ins.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter className="flex-col sm:flex-row gap-2">
						<Button
							variant="outline"
							onClick={handleDismissForever}
							className="w-full sm:w-auto"
						>
							Don't Show Again
						</Button>
						<Button
							variant="ghost"
							onClick={handleSkipThisTime}
							className="w-full sm:w-auto"
						>
							Skip This Time
						</Button>
						<Button onClick={handleStartTutorial} className="w-full sm:w-auto">
							Start Tutorial
						</Button>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			{/* Completion Dialog */}
			<AlertDialog
				open={showCompletionDialog}
				onOpenChange={setShowCompletionDialog}
			>
				<AlertDialogContent className="sm:max-w-[550px]">
					<AlertDialogHeader>
						<AlertDialogTitle>Ready to Get Started?</AlertDialogTitle>
						<AlertDialogDescription>
							Great! You've completed the tutorial. Would you like to create
							your first reflection (reccomended) or add a daily check-in now?
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter className="flex-col sm:flex-row gap-2">
						<Button
							variant="outline"
							onClick={handleCompletionLater}
							className="w-full sm:w-auto"
						>
							I'll Do This Later
						</Button>
						<Button
							variant="secondary"
							onClick={handleCreateCheckIn}
							className="w-full sm:w-auto"
						>
							<Calendar className="h-4 w-4 mr-2" />
							Add Check-in
						</Button>
						<Button
							onClick={handleCreateReflection}
							className="w-full sm:w-auto"
						>
							<Lightbulb className="h-4 w-4 mr-2" />
							Create Reflection
						</Button>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			<Joyride
				steps={steps}
				run={runTutorial}
				continuous
				showProgress
				showSkipButton
				callback={handleJoyrideCallback}
				locale={{
					back: "Back",
					last: "OK",
				}}
				styles={{
					options: {
						primaryColor: "hsl(var(--primary))",
						zIndex: 10000,
					},
					tooltip: {
						borderRadius: 8,
					},
					buttonNext: {
						borderRadius: 6,
						padding: "8px 16px",
					},
					buttonBack: {
						marginRight: 8,
						color: "hsl(var(--muted-foreground))",
					},
					buttonSkip: {
						color: "hsl(var(--muted-foreground))",
					},
				}}
			/>
		</>
	);
}
